import type {
  IGoogleAuthAdapter,
  GoogleAuthConfig,
  GoogleAuthResult,
  ISettingsRepository,
  ICryptoAdapter,
} from '@app/domain/ports';

// Local constant to avoid runtime import from @app/domain/ports
const GOOGLE_AUTH_TOKENS_KEY = 'runtime:google_auth_tokens';

interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
  expiryTime: number;
}

/**
 * Google OAuth 2.0 authentication adapter implementation
 * Uses OAuth Code Flow with PKCE for secure authentication
 * Pure TypeScript implementation - framework-agnostic
 */
export class GoogleAuthAdapter implements IGoogleAuthAdapter {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private expiryTime: number | null = null;

  constructor(
    private readonly settings: ISettingsRepository,
    private readonly configOrFn: GoogleAuthConfig | (() => GoogleAuthConfig),
    private readonly crypto: ICryptoAdapter,
  ) {}

  private get config(): GoogleAuthConfig {
    return typeof this.configOrFn === 'function' ? this.configOrFn() : this.configOrFn;
  }

  initialize(): void {
    this.tryRestoreCachedToken();
  }

  isAuthorized(): boolean {
    return this.getAccessToken() !== null;
  }

  getAccessToken(): string | null {
    // Check if token is expired
    if (this.accessToken && this.expiryTime && Date.now() >= this.expiryTime) {
      console.log('⚠ Access token expired');
      this.accessToken = null;
    }
    return this.accessToken;
  }

  async requestConsent(customState?: Record<string, unknown>): Promise<void> {
    console.log('Starting Google Drive consent flow...');
    await this.authorizeWithCodeFlow({ prompt: 'consent', ...(customState && { customState }) });
  }

  async authorize(
    opts: {
      prompt?: 'none' | 'consent' | 'select_account';
      customState?: Record<string, unknown>;
      silent?: boolean;
    } = {
      prompt: 'select_account',
    },
  ): Promise<boolean> {
    // If already authorized, return current state
    if (this.isAuthorized()) return true;

    // Try refresh token first if available
    if (this.refreshToken) {
      console.log('Attempting token refresh...');
      const refreshed = await this.refreshAccessToken();
      if (refreshed) return true;
      console.log('Token refresh failed');
    }

    // In silent mode, don't start OAuth flow - just return false
    if (opts.silent) {
      console.log('Silent mode: OAuth redirect prevented');
      return false;
    }

    // Start OAuth Code Flow with PKCE
    console.log('Starting OAuth flow...');
    await this.authorizeWithCodeFlow(opts);
    return false;
  }

  async handleOAuthCallback(code: string, encodedState: string): Promise<GoogleAuthResult | null> {
    console.log('[GoogleAuth] handleOAuthCallback called, code length:', code.length);

    // Decode and parse state
    let stateData: { oauth: string; [key: string]: unknown };
    try {
      stateData = JSON.parse(atob(encodedState));
    } catch (error) {
      console.error('[GoogleAuth] Invalid state encoding', error);
      return null;
    }

    // Verify OAuth state to prevent CSRF
    const savedState = sessionStorage.getItem('oauth_state');
    console.log(
      '[GoogleAuth] state check — incoming:',
      stateData.oauth?.substring(0, 8) + '…',
      '| saved in sessionStorage:',
      savedState ? savedState.substring(0, 8) + '…' : '(null)',
    );
    if (stateData.oauth !== savedState) {
      console.error('[GoogleAuth] OAuth state mismatch - possible CSRF attack or sessionStorage was lost');
      return null;
    }

    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    console.log('[GoogleAuth] pkce_code_verifier present:', !!codeVerifier);
    if (!codeVerifier) {
      console.error('[GoogleAuth] No code verifier found');
      return null;
    }

    const functionsUrl = `${this.config.functionsUrl}/exchangeGoogleToken`;
    console.log('[GoogleAuth] Exchanging token via:', functionsUrl, '| redirectUri:', this.config.redirectUri);

    try {
      // Exchange authorization code for tokens using Firebase Function
      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          codeVerifier,
          redirectUri: this.config.redirectUri,
        }),
      });

      console.log('[GoogleAuth] Token exchange response status:', response.status);
      if (!response.ok) {
        console.error('[GoogleAuth] Token exchange failed:', response.status);
        // Return custom state even on failure so caller can handle redirect
        return { customState: stateData };
      }

      const tokens = await response.json();
      console.log(
        '[GoogleAuth] Token exchange success — access_token present:',
        !!tokens.access_token,
        '| refresh_token present:',
        !!tokens.refresh_token,
      );

      // Store tokens
      this.setTokens(tokens.access_token, tokens.refresh_token, tokens.expires_in);

      // Clean up session storage
      sessionStorage.removeItem('pkce_code_verifier');
      sessionStorage.removeItem('oauth_state');

      console.log('✓ OAuth code flow completed successfully');
      return {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        customState: stateData,
      };
    } catch (error) {
      console.error('Token exchange error:', error);
      return { customState: stateData };
    }
  }

  /**
   * OAuth Code Flow with PKCE (provides refresh tokens)
   */
  private async authorizeWithCodeFlow(opts?: {
    prompt?: 'none' | 'consent' | 'select_account';
    customState?: Record<string, unknown>;
  }): Promise<void> {
    const config = this.config;
    const prompt = opts?.prompt ?? 'select_account';
    const preparedCustomState = config.prepareCustomState
      ? await config.prepareCustomState(opts?.customState)
      : undefined;
    const customState = preparedCustomState ?? opts?.customState;

    // Generate PKCE parameters
    const codeVerifier = this.crypto.generatePkceVerifier();
    const codeChallenge = await this.crypto.generatePkceChallenge(codeVerifier);
    const oauthState = this.crypto.generateRandomString(32);

    // Combine OAuth state with custom state
    const stateData = {
      oauth: oauthState,
      ...customState,
    };
    const encodedState = btoa(JSON.stringify(stateData));

    // Store PKCE parameters and OAuth state for callback verification
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    sessionStorage.setItem('oauth_state', oauthState);

    // Build authorization URL
    const params = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      response_type: 'code',
      scope: config.scopes,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state: encodedState,
      access_type: 'offline', // Request refresh token
      prompt, // 'consent' forces refresh token, 'select_account' for returning users
    });

    if (config.authorizationLanguage) {
      params.set('hl', config.authorizationLanguage);
    }

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;

    // Open OAuth URL — use the provided callback (Capacitor: Custom Tab) or fall back to full-page redirect (web)
    if (config.openUrl) {
      await config.openUrl(authUrl);
    } else {
      window.location.href = authUrl;
    }
  }

  /**
   * Refresh access token using refresh token
   * This works reliably on mobile without popup blockers
   */
  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) return false;

    try {
      // Refresh access token using Firebase Function
      const response = await fetch(`${this.config.functionsUrl}/refreshGoogleToken`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refreshToken: this.refreshToken,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Token refresh failed:', response.status, error);

        // Only clear refresh token if it's actually invalid (401/400)
        // Don't clear on network errors (500, timeout, etc.)
        if (response.status === 401 || response.status === 400) {
          console.log('⚠ Refresh token invalid or revoked, clearing tokens');
          this.clearCachedTokens();
        }
        return false;
      }

      const tokens = await response.json();

      // Update access token
      // Google may return a new refresh token, but usually doesn't during refresh
      this.setTokens(tokens.access_token, tokens.refresh_token, tokens.expires_in);

      console.log('✓ Token refreshed successfully');
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      // Don't clear tokens on network errors - keep refresh token for retry
      return false;
    }
  }

  private setTokens(accessToken: string, refreshToken: string | undefined, expiresIn?: number): void {
    this.accessToken = accessToken;

    // Only update refresh token if explicitly provided (don't overwrite with undefined/null)
    if (refreshToken) {
      this.refreshToken = refreshToken;
    }

    this.expiryTime = Date.now() + (expiresIn || 3600) * 1000;

    const stored: StoredTokens = {
      accessToken,
      expiryTime: this.expiryTime,
    };

    // Only store refresh token if it exists
    if (this.refreshToken) {
      stored.refreshToken = this.refreshToken;
    }

    this.settings.set(GOOGLE_AUTH_TOKENS_KEY, stored);

    console.log('✓ Google authorized' + (this.refreshToken ? ' with refresh token support' : ''));
  }

  private tryRestoreCachedToken(): void {
    try {
      const stored = this.settings.get<StoredTokens>(GOOGLE_AUTH_TOKENS_KEY);
      if (!stored) return;

      // Check if token is still valid (with 5 min buffer)
      if (Date.now() < stored.expiryTime - 5 * 60 * 1000) {
        this.accessToken = stored.accessToken;
        this.refreshToken = stored.refreshToken || null;
        this.expiryTime = stored.expiryTime;
        console.log('✓ Restored cached Google token');
      } else if (stored.refreshToken) {
        // Token expired but we have refresh token - keep it for refresh
        this.refreshToken = stored.refreshToken;
        this.expiryTime = stored.expiryTime;
        console.warn('⚠ Access token expired, will refresh on next use');
      } else {
        // No refresh token available, clear everything
        this.clearCachedTokens();
      }
    } catch (error) {
      // Parse error - try to preserve refresh token if possible
      console.error('Failed to restore cached token:', error);
      this.clearCachedTokens();
    }
  }

  private clearCachedTokens(): void {
    this.settings.remove(GOOGLE_AUTH_TOKENS_KEY);
    this.accessToken = null;
    this.refreshToken = null;
    this.expiryTime = null;
  }
}
